import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users, Truck, ShieldAlert, RotateCcw, Boxes, AlertTriangle, ArrowRight, PackageX, Wrench, Receipt, ScanLine, Mail, BookOpen, Eye, Loader2 } from "lucide-react";
import { DISPOSITION_LABEL } from "@/lib/returns";
import ReturnsAnalytics from "@/components/ReturnsAnalytics";

function Kpi({ label, value, sub, icon: Icon, tone = "slate", onClick, testId }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600",
    blue: "bg-blue-100 text-blue-600",
    orange: "bg-orange-100 text-orange-600",
    green: "bg-emerald-100 text-emerald-600",
    red: "bg-red-100 text-red-600",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left bg-white border border-slate-200 rounded-xl p-4 transition-all ${onClick ? "hover:border-blue-400 hover:shadow-sm cursor-pointer" : "cursor-default"}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
    </button>
  );
}

export function AdminOverview({ accounts = [], users = [], onGoto }) {
  const [claims, setClaims] = useState([]);
  const [claimsError, setClaimsError] = useState(false);
  useEffect(() => {
    api.get("/claims")
      .then(({ data }) => { setClaims(data.claims || []); setClaimsError(false); })
      .catch(() => { setClaims([]); setClaimsError(true); });
  }, []);

  const activeUsers = users.filter((u) => u.status === "active").length;
  const pendingUsers = users.filter((u) => u.status === "pending").length;
  const linkedStores = accounts.filter((a) => a.shipstation_store_id || a.shipstation_customer_id).length;
  const unlinkedAccounts = accounts.filter((a) => !a.shipstation_store_id && !a.shipstation_customer_id);
  const noStripe = accounts.filter((a) => !a.stripe_customer_id);
  const openClaims = claims.filter((c) => !["credit_applied", "denied"].includes(c.status));

  const go = (v) => onGoto && onGoto(v);

  const attention = [];
  if (pendingUsers > 0) attention.push({ id: "pending", text: `${pendingUsers} user${pendingUsers === 1 ? "" : "s"} awaiting approval`, action: () => go("users"), cta: "Review users" });
  if (claimsError) attention.push({ id: "claims-error", text: "Couldn't load claims — open the Claims tab to retry", action: () => go("claims"), cta: "Open claims" });
  else if (openClaims.length > 0) attention.push({ id: "claims", text: `${openClaims.length} open claim${openClaims.length === 1 ? "" : "s"} to action`, action: () => go("claims"), cta: "Open claims" });
  if (unlinkedAccounts.length > 0) attention.push({ id: "unlinked", text: `${unlinkedAccounts.length} account${unlinkedAccounts.length === 1 ? "" : "s"} not linked to ShipStation`, action: () => go("accounts"), cta: "Fix accounts" });
  if (noStripe.length > 0) attention.push({ id: "stripe", text: `${noStripe.length} account${noStripe.length === 1 ? "" : "s"} without a Stripe customer`, action: () => go("accounts"), cta: "Link Stripe" });

  return (
    <div data-testid="admin-overview" className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Operations overview</h2>
        <p className="text-sm text-slate-500 mt-0.5">A quick pulse on your customer base and what needs attention today.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi label="Customer accounts" value={accounts.length} icon={Building2} tone="blue" onClick={() => go("accounts")} testId="kpi-accounts" />
        <Kpi label="Active users" value={activeUsers} sub={pendingUsers ? `${pendingUsers} pending` : "all approved"} icon={Users} tone={pendingUsers ? "orange" : "green"} onClick={() => go("users")} testId="kpi-users" />
        <Kpi label="Stores linked" value={`${linkedStores}/${accounts.length}`} icon={Truck} tone="slate" onClick={() => go("accounts")} testId="kpi-stores" />
        <Kpi label="Open claims" value={claimsError ? "—" : openClaims.length} sub={claimsError ? "load error" : `${claims.length} total`} icon={ShieldAlert} tone={claimsError ? "red" : openClaims.length ? "orange" : "green"} onClick={() => go("claims")} testId="kpi-claims" />
        <Kpi label="Need Stripe" value={noStripe.length} icon={Receipt} tone={noStripe.length ? "red" : "green"} onClick={() => go("accounts")} testId="kpi-stripe" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          <h3 className="text-base font-bold text-slate-900">Needs attention</h3>
        </div>
        {attention.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">All caught up — no pending items right now. Nice work. 🎉</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="attention-list">
            {attention.map((a) => (
              <li key={a.id} className="flex items-center justify-between py-3">
                <span className="text-sm text-slate-700">{a.text}</span>
                <button onClick={a.action} data-testid={`attention-${a.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700">
                  {a.cta} <ArrowRight className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Small local receipt icon alias to avoid an extra import churn
function ComingSoonPanel({ icon: Icon, title, blurb, bullets, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl p-8">
      <div className="max-w-xl">
        <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{title}</h2>
          <span className="text-[10px] uppercase tracking-wider font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded">Coming soon</span>
        </div>
        <p className="text-sm text-slate-500 mt-2">{blurb}</p>
        <ul className="mt-4 space-y-2">
          {bullets.map((b, i) => (
            <li key={`${b}-${i}`} className="flex items-start gap-2 text-sm text-slate-600">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" /> {b}
            </li>
          ))}
        </ul>
        <button data-testid={`${testId}-in-development`} disabled className="mt-6 inline-flex items-center gap-2 bg-slate-100 text-slate-400 font-semibold px-4 py-2 rounded-md cursor-not-allowed">
          <Wrench className="w-4 h-4" /> In development
        </button>
      </div>
    </div>
  );
}

export function AdminReturns() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [store, setStore] = useState("");
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    api.get("/staff/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {});
  }, []);

  const load = (id) => {
    if (!id) { setReturns([]); return; }
    setLoading(true);
    setError(false);
    api.get(`/returns?account_id=${id}`)
      .then(({ data }) => setReturns(data.returns || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  const onStore = (e) => { const v = e.target.value; setStore(v); setPreview(null); load(v); };

  const loadPreview = async () => {
    if (!store) return;
    setPreviewing(true);
    try {
      const { data } = await api.get(`/admin/accounts/${store}/returns/digest/preview`);
      setPreview(data);
    } catch (e) {
      toast.error("Could not build preview");
    } finally {
      setPreviewing(false);
    }
  };

  const sendDigest = async () => {
    if (!store) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/accounts/${store}/returns/digest`);
      if (data.returns === 0) toast.info("No returns processed today for this store — nothing to send.");
      else if (data.sent > 0) toast.success(`Digest sent for ${data.returns} return(s).`);
      else toast.warning("No recipients configured for this store.");
    } catch (e) {
      toast.error("Could not send digest");
    } finally {
      setSending(false);
    }
  };

  const exportCsv = async () => {
    if (!store) return;
    try {
      const { data } = await api.get(`/returns/export?account_id=${store}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "returns.csv"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not export");
    }
  };

  const dispSummary = (r) => {
    const m = {};
    (r.items || []).forEach((it) => {
      const d = it.customer_disposition || it.disposition;
      const key = d ? DISPOSITION_LABEL(d) : "Pending";
      m[key] = (m[key] || 0) + (it.qty_returned || 0);
    });
    return Object.entries(m).map(([k, v]) => `${k}: ${v}`).join(", ") || "—";
  };

  return (
    <div data-testid="admin-returns" className="space-y-6">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold" style={{ fontFamily: "Manrope" }}>Returns Receiving Station</h2>
          <p className="text-white/60 text-sm mt-1 max-w-lg">Touchscreen workflow for the warehouse — scan a return, inspect each item, record its condition and capture photos. The brand sets the disposition from their dashboard. Best on the big monitor or an iPad.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate("/training?topic=returns")}
            data-testid="open-training-returns"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-4 py-4 rounded-2xl"
          >
            <BookOpen className="w-5 h-5" /> How-to guide
          </button>
          <button
            onClick={() => navigate("/receiving")}
            data-testid="launch-receiving-btn"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg px-7 py-4 rounded-2xl"
          >
            <ScanLine className="w-6 h-6" /> Launch station
          </button>
        </div>
      </div>

      <ReturnsAnalytics />

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <PackageX className="w-5 h-5 text-slate-500" />
            <h3 className="text-base font-bold text-slate-900">Recent returns</h3>
          </div>
          <div className="flex items-center gap-2">
            <select value={store} onChange={onStore} data-testid="returns-store-select" className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
              <option value="">Select a store…</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
            </select>
            <button onClick={exportCsv} disabled={!store} data-testid="returns-export-admin-btn"
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md">
              Export CSV
            </button>
            <button onClick={loadPreview} disabled={!store || previewing} data-testid="preview-digest-btn"
              className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white text-sm font-semibold px-3 py-2 rounded-md">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview digest
            </button>
            <button onClick={sendDigest} disabled={!store || sending} data-testid="send-digest-btn"
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-3 py-2 rounded-md">
              <Mail className="w-4 h-4" /> {sending ? "Sending…" : "Send digest"}
            </button>
          </div>
        </div>

        {preview && (
          <div className="mb-5 border border-slate-200 rounded-xl p-4 bg-slate-50" data-testid="returns-digest-preview">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider font-medium text-slate-500">Daily digest preview — {preview.date}</p>
              <span className="text-xs text-slate-500">{preview.count} return(s) · {preview.recipients.length} recipient(s)</span>
            </div>
            {preview.count === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">No returns processed on {preview.date} — nothing would be sent (empty days are skipped).</p>
            ) : (
              <iframe title="Returns digest preview" srcDoc={preview.html} sandbox="" data-testid="returns-digest-frame"
                className="w-full h-[520px] border border-slate-200 rounded-lg bg-[#F0F2F1]" />
            )}
          </div>
        )}

        {!store ? (
          <p className="text-sm text-slate-400 py-8 text-center">Pick a store to see returns processed for it.</p>
        ) : loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">Loading…</p>
        ) : error ? (
          <div className="py-8 text-center">
            <p className="text-sm font-semibold text-red-600">Couldn't load returns.</p>
            <button onClick={() => load(store)} data-testid="admin-returns-retry" className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700">Retry</button>
          </div>
        ) : returns.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No returns recorded for this store yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="admin-returns-table">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                  <th className="py-2.5 px-3 font-medium">Order #</th>
                  <th className="py-2.5 px-3 font-medium">Units</th>
                  <th className="py-2.5 px-3 font-medium">Disposition</th>
                  <th className="py-2.5 px-3 font-medium">Reason</th>
                  <th className="py-2.5 px-3 font-medium">Photos</th>
                  <th className="py-2.5 px-3 font-medium">Received</th>
                </tr>
              </thead>
              <tbody>
                {returns.map((r) => (
                  <tr key={r.id} data-testid={`admin-return-row-${r.id}`} className="border-b border-slate-100">
                    <td className="py-2.5 px-3 font-mono font-semibold text-slate-900">{r.order_number}</td>
                    <td className="py-2.5 px-3 text-slate-700">{(r.items || []).reduce((s, it) => s + (it.qty_returned || 0), 0)}</td>
                    <td className="py-2.5 px-3 text-slate-600">{dispSummary(r)}</td>
                    <td className="py-2.5 px-3 text-slate-500">{r.reason || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-500">{(r.photos || []).length}</td>
                    <td className="py-2.5 px-3 text-slate-500 whitespace-nowrap">{(r.completed_at || r.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminInventory() {
  return (
    <ComingSoonPanel
      icon={Boxes}
      title="Inventory by store"
      testId="admin-inventory"
      blurb="See and manage on-hand inventory for each store you fulfill — SKUs, quantities, low-stock alerts and adjustments."
      bullets={[
        "Live on-hand quantities per SKU, grouped by store",
        "Low-stock and out-of-stock alerts",
        "Manual adjustments with an audit trail",
        "Sync counts from ShipStation / your WMS",
      ]}
    />
  );
}
